/**
 * countUp — ease-out cubic count-up animation helper.
 *
 * Counts an integer from 0 to `target` over `duration` ms using requestAnimationFrame,
 * calling `onFrame` with the current (rounded) value each frame.
 *
 * Respects `prefers-reduced-motion`: when the user has requested reduced motion,
 * `onFrame` is called exactly once with the final value (no animation).
 *
 * Returns a cancel function — call it to abort mid-animation (e.g. on component
 * unmount). After cancellation, `onFrame` is never called again.
 *
 * Usage:
 *   const cancel = countUp(42, (v) => { el.textContent = String(v); });
 *   // on unmount:
 *   cancel();
 */

/** Ease-out cubic: fast start, decelerates to final value. */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** Whether the current environment has requested reduced motion. */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Animate counting from 0 to `target` (integer) over `duration` ms.
 *
 * @param target   Final integer value to count up to.
 * @param onFrame  Called with the current rounded value each animation frame
 *                 (and once with the final value when done).
 * @param duration Animation duration in milliseconds (default 1100ms per design intent).
 * @returns        A cancel function — call on unmount to abort.
 */
export function countUp(
  target: number,
  onFrame: (value: number) => void,
  duration = 1100,
): () => void {
  // Snap to final value immediately under reduced motion.
  if (prefersReducedMotion()) {
    onFrame(Math.round(target));
    return () => {};
  }

  // Zero target: nothing to animate.
  if (target <= 0) {
    onFrame(0);
    return () => {};
  }

  let rafId: number | null = null;
  let cancelled = false;
  const start = performance.now();

  function frame(now: number) {
    if (cancelled) return;

    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easeOutCubic(progress);
    const current = Math.round(eased * target);

    onFrame(current);

    if (progress < 1) {
      rafId = requestAnimationFrame(frame);
    }
  }

  rafId = requestAnimationFrame(frame);

  return () => {
    cancelled = true;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };
}

import { useState, useEffect } from "react";

/**
 * React hook: animates an integer from 0 to `target` over `durationMs` using
 * the countUp helper. Respects prefers-reduced-motion.
 *
 * @param target     Final value to count to. Re-triggers animation when it changes.
 * @param durationMs Animation duration in ms (default 1100).
 * @param skip       When `true`, snap straight to `target` with no animation.
 *                   Used when the value is revealed some other way (e.g. the
 *                   detail hero arrived via the shared-element morph — the morph
 *                   IS the reveal, so a second count-up would be redundant).
 * @returns          Current animated integer value.
 */
export function useCountUp(target: number, durationMs = 1100, skip = false): number {
  const [value, setValue] = useState<number>(() => (skip || prefersReducedMotion() ? target : 0));

  useEffect(() => {
    if (skip) {
      // Snap to target when skip/target change post-mount (the initial mount already
      // starts at target via the useState initialiser). The animated path below sets
      // state from rAF callbacks (allowed); only this synchronous snap trips the rule.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional immediate snap
      setValue(target);
      return;
    }
    const cancel = countUp(target, setValue, durationMs);
    return cancel;
  }, [target, durationMs, skip]);

  return value;
}
